import { useCallback, useMemo, useState } from 'react'
import { useUserConverts } from '../state'
import useWallet from './useWallet'
import { useConvertContext } from '../providers/ConvertProvider'
import { useGoogleAnalytics } from '../providers/GoogleAnalyticsProvider'
import { useTranslation } from 'react-i18next'
import useStealthex from './useStealthex'
import useConfig from './useConfig'
import { ExchangeApiResponse } from '../clients/Stealthex'
import { ExchangeActivity, ExchangeProvider } from '../interfaces/transactionActivity'
import { GAEventName } from '../interfaces/googleAnalytics'
import { sendSol, transferToken } from '../handlers/walletActions/wallet'
import { decimalsToE8s } from '../utils/validationHelper'
import { ICP_LEDGER } from '../constants/canisterIds'
import { logger } from '../logger'
import { Chain } from '../interfaces/common'

const SUPPORTED_CHAINS = ['icp', 'sol', 'eth', 'btc']

interface Props {
  onError?: (error: string) => void
  onSuccess: (convertId?: string) => void
}

const useConvertReview = ({ onError, onSuccess }: Props) => {
  const [destinationAddress, setDestinationAddress] = useState<string>('')
  const [loading, setLoading] = useState(false)

  const { wallet } = useWallet()
  const { addConvert } = useUserConverts()
  const { fireEvent } = useGoogleAnalytics()

  const { fromToken, toToken, amount, toAmount } = useConvertContext()

  const { t } = useTranslation()

  const { createExchange } = useStealthex({
    from: fromToken,
    to: toToken,
  })

  const { tokens } = useConfig()
  const erc20Tokens = tokens
    .filter((t) => t.chain === Chain.ETH && t.standard === 'ERC20')
    .map((t) => t.symbol.toLowerCase())

  const destinationAddressValid = useMemo(() => {
    if (!toToken?.addressValidation || !destinationAddress) return true

    const regex = new RegExp(toToken.addressValidation)
    return regex.test(destinationAddress || '')
  }, [toToken, destinationAddress])

  const requiresAddressInput = useMemo(() => {
    if (!toToken) return false

    return ![...SUPPORTED_CHAINS, ...erc20Tokens].includes(toToken.id)
  }, [toToken, tokens])

  const error = useMemo(() => {
    if (requiresAddressInput && !destinationAddress)
      return t('convert.errors.noAddress', {
        currency: toToken?.symbol,
      })

    if (!destinationAddressValid) {
      return t('convert.errors.addressInvalid', {
        currency: toToken?.symbol,
      })
    }
  }, [destinationAddress, t, toToken, destinationAddressValid, requiresAddressInput])

  const saveConvert = (exchange: ExchangeApiResponse) => {
    const convert: ExchangeActivity = {
      exchangeId: exchange.data.id,
      fromToken: {
        symbol: fromToken!.id,
        logo: fromToken!.logo,
      },
      toToken: {
        symbol: toToken!.id,
        logo: toToken!.logo,
      },
      fromAmount: amount!,
      toAmount: toAmount!,
      timestamp: new Date(),
      provider: ExchangeProvider.StealthEx,
      wallet: wallet.principalId,
    }

    addConvert(wallet.principalId, convert)
  }

  const getWalletAddress = useCallback(
    (tokenId: string) => {
      switch (tokenId) {
        case 'icp':
          return wallet.accountId
        case 'eth':
          return wallet.ethAddress
        case 'btc':
          return wallet.btcAddress
        case 'sol':
          return wallet.solAddress
      }

      if (erc20Tokens.includes(tokenId)) return wallet.ethAddress
    },
    [wallet, erc20Tokens]
  )

  const performConvert = useCallback(async () => {
    if (!fromToken || !toToken) return

    try {
      const exchange = await createExchange(
        parseFloat(amount || ''),
        getWalletAddress(toToken.id) || destinationAddress
      )

      fireEvent(GAEventName.SWAP, {
        type: 'conversion',
        from_token: fromToken!.symbol,
        to_token: toToken!.symbol,
        amount: Number(amount),
        amount_received: Number(toAmount),
      })

      switch (exchange.data?.currency_from) {
        case 'icp':
          await transferToken({
            receiver: { address: exchange.data.address_from },
            amount: decimalsToE8s(amount || '', 8),
            tokenCanisterId: ICP_LEDGER,
            standard: 'ICP',
          })
          break
        case 'sol':
          await sendSol({
            to: exchange.data.address_from,
            from: wallet.solAddress || '',
            amount: decimalsToE8s(amount || '', 9),
          })
          break
        default:
          break
      }

      saveConvert(exchange)

      return exchange.data.id
    } catch (e) {
      logger.error('Convert error', { originalError: e })

      return Promise.reject('Transfer error')
    }
  }, [createExchange, amount, destinationAddress, wallet, toToken, getWalletAddress])

  const convert = useCallback(() => {
    setLoading(true)

    performConvert()
      .then(onSuccess)
      .catch((error) => {
        onError?.(error)
      })
      .finally(() => setLoading(false))
  }, [performConvert, setLoading])

  return {
    loading,
    convert,
    requiresAddressInput,
    destinationAddress,
    setDestinationAddress,
    error,
    destinationAddressValid,
  }
}

export default useConvertReview
